<template>
    <div v-if="isModalOpen" class="fixed left-0 top-0 z-50 flex size-full items-center justify-center overflow-hidden bg-neutral-700/50 dark:bg-neutral-700/80">
        <dialog open closedby="any" :class="['relative flex max-h-[95vh] flex-col bg-transparent text-neutral-700 dark:text-neutral-100', dialogSizeClass, dialogClass]" @close="close">
            <!-- Modal content -->
            <div class="relative flex min-h-0 flex-col overflow-hidden rounded-lg bg-neutral-100 shadow dark:bg-neutral-700">
                <countdown-bar v-if="isRunning" class="h-2" :progress="progress" :tick-rate-ms="timerTickRate" />
                <!-- Modal header -->
                <header class="flex items-center justify-between rounded-t border-b border-neutral-300 p-4 md:p-5 dark:border-neutral-600">
                    <slot name="header">
                        <div>
                            <div class="flex items-center">
                                <ion-icon v-if="titleIcon" :class="headerTextColorClass" :icon="titleIcon" class="mr-2" size="large"></ion-icon>
                                <span :class="headerTextColorClass" class="text-2xl font-semibold">{{ title }}</span>
                            </div>
                            <p v-if="isRunning" class="block text-neutral-500 dark:text-neutral-400">{{ t("autoCloseIn", { seconds: remainingSeconds }) }}</p>
                        </div>
                    </slot>

                    <button class="ms-auto inline-flex size-8 items-center justify-center rounded-lg bg-transparent text-sm text-neutral-800 dark:text-neutral-400" @click="close">
                        <ion-icon :icon="closeOutline" size="large" />
                    </button>
                </header>
                <!-- Modal body -->
                <div :class="bodyClass" class="flex min-h-0 flex-col space-y-4 overflow-hidden p-4 text-xl md:p-5">
                    <slot name="body" />
                </div>

                <!-- Modal footer -->
                <footer v-if="$slots.footer" class="flex items-center rounded-b border-t border-neutral-300 p-4 md:p-5 dark:border-neutral-600">
                    <slot name="footer" />
                </footer>
            </div>
        </dialog>
    </div>
</template>

<script lang="ts" setup>
    import { closeOutline } from "ionicons/icons";
    import { IonIcon } from "@ionic/vue";
    import { computed, watch } from "vue";
    import { Temporal } from "temporal-polyfill";
    import { useI18n } from "vue-i18n";
    import CountdownBar from "../CountdownBar.vue";
    import { useAutoDismiss } from "@/composables/useAutoDismiss";

    const { t } = useI18n({ useScope: "global" });

    const isModalOpen = defineModel("isOpen", { type: Boolean, required: true });

    type Props = {
        title?: string;
        titleIcon?: string;
        type?: "default" | "success" | "error";
        /** Controls the max-width of the dialog: small=28rem, medium=56rem, large=64rem, fit=content width.
         * All sizes cap at 95vw. */
        size?: "small" | "medium" | "large" | "fit";
        dialogClass?: string;
        bodyClass?: string;
    };

    const props = withDefaults(defineProps<Props>(), {
        title: "",
        titleIcon: undefined,
        type: "default",
        size: "fit",
        bodyClass: undefined,
        dialogClass: undefined,
    });
    const emit = defineEmits(["did-close"]);

    const dialogSizeClass = computed(() => {
        switch (props.size) {
            case "small":
                return "w-full max-w-[min(95vw,28rem)]";
            case "medium":
                return "w-full max-w-[min(95vw,56rem)]";
            case "large":
                return "w-full max-w-[min(95vw,64rem)]";
            case "fit":
                return "w-fit max-w-[95vw]";
            default:
                props.size satisfies never;
                throw new TypeError(`Missing implementation for ${props.size}`);
        }
    });

    const headerTextColorClass = computed(() => {
        switch (props.type) {
            case "success":
                return "text-success";
            case "error":
                return "text-danger";
            default:
                return "text-neutral-800 dark:text-neutral-300";
        }
    });

    const { progress, remainingSeconds, isRunning, start: startTimer, reset: resetTimer, tickRateMs: timerTickRate } = useAutoDismiss(close, { trackProgress: true });

    watch(isModalOpen, (newValue) => {
        if (!newValue) {
            resetTimer();
        }
    });

    defineExpose({ setAutoDismissAfter, stopAutoDismiss });

    function setAutoDismissAfter(duration: Temporal.DurationLike): void {
        startTimer(Temporal.Duration.from(duration));
    }

    function stopAutoDismiss(): void {
        resetTimer();
    }

    function close(): void {
        isModalOpen.value = false;
        emit("did-close");
    }
</script>
